<template>
  <WebLayout
    title="Gestiona tu negocio de forma eficiente"
    description="Administra clientes, productos, inventarios, compras y ventas en un solo lugar. Optimiza tus procesos y toma mejores decisiones con Realgestión."
    url="https://realgestion.lat"
  >
    <Head :title="`Realgestión - Gestiona tu negocio de forma eficiente`" />
    <!-- Hero Section -->
    <div class="relative bg-white overflow-hidden">
      <div class="max-w-7xl mx-auto">
        <div class="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
          <main class="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
            <div class="sm:text-center lg:text-left">
              <h1 class="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                <span class="block xl:inline">Gestiona tu negocio</span> <br>
                <span class="block text-teal-600 xl:inline">de forma eficiente</span>
              </h1>
              <p class="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                Administra clientes, productos, inventarios, compras y ventas en un solo lugar. Optimiza tus procesos y toma mejores decisiones con Realgestión.
              </p>
              <div class="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                <div class="rounded-md shadow">
                  <Link href="/register" class="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 md:py-4 md:text-lg md:px-10">
                    Comenzar gratis
                  </Link>
                </div>
                <div class="mt-3 sm:mt-0 sm:ml-3">
                  <Link href="#features" class="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-teal-700 bg-teal-100 hover:bg-teal-200 md:py-4 md:text-lg md:px-10">
                    Ver funcionalidades
                  </Link>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <div class="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
        <img class="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full" src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2850&q=80" alt="">
      </div>
    </div>

    <!-- Features Section -->
    <div id="features" class="py-12 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="lg:text-center">
          <h2 class="text-base text-teal-600 font-semibold tracking-wide uppercase">Funcionalidades</h2>
          <p class="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Todo lo que necesitas para tu negocio
          </p>
          <p class="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
            Herramientas poderosas para gestionar cada aspecto de tu empresa
          </p>
        </div>

        <div class="mt-10">
          <div class="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
            <div class="relative">
              <div class="absolute flex items-center justify-center h-12 w-12 rounded-md bg-teal-500 text-white">
                <i class="fas fa-users text-xl"></i>
              </div>
              <p class="ml-16 text-lg leading-6 font-medium text-gray-900">Gestión de Clientes</p>
              <p class="mt-2 ml-16 text-base text-gray-500">
                Administra tu base de clientes, historial de compras y saldos pendientes de forma eficiente.
              </p>
            </div>

            <div class="relative">
              <div class="absolute flex items-center justify-center h-12 w-12 rounded-md bg-teal-500 text-white">
                <i class="fas fa-box text-xl"></i>
              </div>
              <p class="ml-16 text-lg leading-6 font-medium text-gray-900">Control de Inventario</p>
              <p class="mt-2 ml-16 text-base text-gray-500">
                Mantén un control preciso de tu inventario con alertas de stock bajo y seguimiento de productos.
              </p>
            </div>

            <div class="relative">
              <div class="absolute flex items-center justify-center h-12 w-12 rounded-md bg-teal-500 text-white">
                <i class="fas fa-shopping-cart text-xl"></i>
              </div>
              <p class="ml-16 text-lg leading-6 font-medium text-gray-900">Ventas y Compras</p>
              <p class="mt-2 ml-16 text-base text-gray-500">
                Procesa ventas y compras, genera facturas y mantén un registro detallado de todas tus transacciones.
              </p>
            </div>

            <div class="relative">
              <div class="absolute flex items-center justify-center h-12 w-12 rounded-md bg-teal-500 text-white">
                <i class="fas fa-chart-line text-xl"></i>
              </div>
              <p class="ml-16 text-lg leading-6 font-medium text-gray-900">Reportes y Análisis</p>
              <p class="mt-2 ml-16 text-base text-gray-500">
                Obtén insights valiosos con reportes detallados y análisis de tu negocio.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Testimonials Section -->
    <div id="testimonials" class="bg-gray-50 pt-16 lg:py-24">
      <div class="pb-16 bg-teal-600 lg:pb-0 lg:z-10 lg:relative">
        <div class="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-3 lg:gap-8">
          <div class="relative lg:-my-8">
            <div class="mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:p-0 lg:h-full">
              <div class="aspect-w-10 aspect-h-6 rounded-xl shadow-xl overflow-hidden sm:aspect-w-16 sm:aspect-h-7 lg:aspect-none lg:h-full">
                <img class="object-cover lg:h-full lg:w-full" src="https://images.unsplash.com/photo-1520333789090-1afc82db536a?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2102&q=80" alt="">
              </div>
            </div>
          </div>
          <div class="mt-12 lg:m-0 lg:col-span-2 lg:pl-8">
            <div class="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:px-0 lg:py-20 lg:max-w-none">
              <blockquote>
                <div>
                  <svg class="h-12 w-12 text-white opacity-25" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
                    <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                  </svg>
                  <p class="mt-6 text-2xl font-medium text-white">
                    Realgestión ha transformado la forma en que manejamos nuestro negocio. La gestión de clientes y el control de inventario son ahora mucho más eficientes.
                  </p>
                </div>
                <footer class="mt-6">
                  <p class="text-base font-medium text-white">María González</p>
                  <p class="text-base font-medium text-teal-100">Emprendedora Venezolana</p>
                </footer>
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div id="pricing" class="bg-white">
      <div class="pt-12 sm:pt-16 lg:pt-24">
        <div class="max-w-7xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div class="max-w-3xl mx-auto space-y-2 lg:max-w-none">
            <h2 class="text-lg leading-6 font-semibold text-gray-600 uppercase tracking-wider">Precios</h2>
            <p class="text-3xl font-extrabold text-gray-900 sm:text-4xl lg:text-5xl">
              El plan perfecto para tu negocio
            </p>
            <p class="text-xl text-gray-500">
              Comienza con un plan gratuito y escala según tus necesidades
            </p>
          </div>
        </div>
      </div>
      <div class="mt-8 pb-12 bg-white sm:mt-12 sm:pb-16 lg:pb-24">
        <div class="relative">
          <div class="absolute inset-0 h-3/4 bg-gray-50"></div>
          <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="max-w-md mx-auto space-y-4 lg:max-w-5xl lg:grid lg:grid-cols-4 lg:gap-5 lg:space-y-0">
              <!-- Plan Gratis -->
              <div class="flex flex-col rounded-lg shadow-lg divide-y divide-gray-200">
                <div class="p-6">
                  <h2 class="text-2xl leading-6 font-semibold text-gray-900">Gratis</h2>
                  <p class="mt-4 text-sm text-gray-500">Perfecto para comenzar</p>
                  <p class="mt-8">
                    <span class="text-4xl font-extrabold text-gray-900">$0</span>
                    <span class="text-base font-medium text-gray-500">/mes</span>
                  </p>
                  <Link href="/register" class="mt-8 block w-full bg-teal-600 border border-transparent rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-teal-700">
                    Registrate gratis
                  </Link>
                </div>
                <div class="pt-6 pb-8 px-6">
                  <h3 class="text-xs font-medium text-gray-900 tracking-wide uppercase">Lo que incluye</h3>
                  <ul class="mt-6 space-y-4">
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Un único usuario</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de clientes</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de productos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Catálogo online de productos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de ventas</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de inventario</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de reportes</span>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Plan Emprendedor -->
              <div class="flex flex-col rounded-lg shadow-lg divide-y divide-gray-200">
                <div class="p-6">
                  <h2 class="text-2xl leading-6 font-semibold text-gray-900">Emprendedor</h2>
                  <p class="mt-4 text-sm text-gray-500">Para negocios en crecimiento</p>
                  <p class="mt-8">
                    <span class="text-4xl font-extrabold text-gray-900">$15</span>
                    <span class="text-base font-medium text-gray-500">/mes</span>
                  </p>
                  <a href="https://wa.me/584244010195?text=Hola%2C%20estoy%20interesado%20en%20el%20plan%20Emprendedor%20de%20RealGestion.%20%C2%BFPodr%C3%ADas%20ayudarme%20con%20la%20suscripci%C3%B3n%3F" target="_blank" class="mt-8 block w-full bg-teal-600 border border-transparent rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-teal-700">
                    Contactar por WhatsApp
                  </a>
                </div>
                <div class="pt-6 pb-8 px-6">
                  <h3 class="text-xs font-medium text-gray-900 tracking-wide uppercase">Lo que incluye</h3>
                  <ul class="mt-6 space-y-4">
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Hasta 3 usuarios</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de clientes</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de productos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Catálogo online de productos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de ventas</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de compras</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de proveedores</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de gastos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de inventario</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de reportes</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Soporte por chat</span>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Plan Pymes -->
              <div class="flex flex-col rounded-lg shadow-lg divide-y divide-gray-200">
                <div class="p-6">
                  <h2 class="text-2xl leading-6 font-semibold text-gray-900">Pymes</h2>
                  <p class="mt-4 text-sm text-gray-500">Para empresas establecidas</p>
                  <p class="mt-8">
                    <span class="text-4xl font-extrabold text-gray-900">$30</span>
                    <span class="text-base font-medium text-gray-500">/mes</span>
                  </p>
                  <a href="https://wa.me/584244010195?text=Hola%2C%20estoy%20interesado%20en%20el%20plan%20Pymes%20de%20RealGestion.%20%C2%BFPodr%C3%ADas%20ayudarme%20con%20la%20suscripci%C3%B3n%3F" target="_blank" class="mt-8 block w-full bg-teal-600 border border-transparent rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-teal-700">
                    Contactar por WhatsApp
                  </a>
                </div>
                <div class="pt-6 pb-8 px-6">
                  <h3 class="text-xs font-medium text-gray-900 tracking-wide uppercase">Lo que incluye</h3>
                  <ul class="mt-6 space-y-4">
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Hasta 10 usuarios</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de clientes</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de productos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Catálogo online de productos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de ventas</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de compras</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de proveedores</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de gastos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de inventario</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de reportes</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Soporte por chat</span>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Plan Empresas -->
              <div class="flex flex-col rounded-lg shadow-lg divide-y divide-gray-200">
                <div class="p-6">
                  <h2 class="text-2xl leading-6 font-semibold text-gray-900">Empresas</h2>
                  <p class="mt-4 text-sm text-gray-500">Para grandes organizaciones</p>
                  <p class="mt-8">
                    <span class="text-4xl font-extrabold text-gray-900">$60</span>
                    <span class="text-base font-medium text-gray-500">/mes</span>
                  </p>
                  <a href="https://wa.me/584244010195?text=Hola%2C%20estoy%20interesado%20en%20el%20plan%20Empresas%20de%20RealGestion.%20%C2%BFPodr%C3%ADas%20ayudarme%20con%20la%20suscripci%C3%B3n%3F" target="_blank" class="mt-8 block w-full bg-teal-600 border border-transparent rounded-md py-2 text-sm font-semibold text-white text-center hover:bg-teal-700">
                    Contactar por WhatsApp
                  </a>
                </div>
                <div class="pt-6 pb-8 px-6">
                  <h3 class="text-xs font-medium text-gray-900 tracking-wide uppercase">Lo que incluye</h3>
                  <ul class="mt-6 space-y-4">
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Usuarios ilimitados</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de clientes</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de productos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Catálogo online de productos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de ventas</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de compras</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de proveedores</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de gastos</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de inventario</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Módulo de reportes</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Soporte dedicado</span>
                    </li>
                    <li class="flex space-x-3">
                      <i class="fas fa-check flex-shrink-0 h-5 w-5 text-teal-500"></i>
                      <span class="text-sm text-gray-500">Personalización</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="bg-teal-700">
      <div class="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
        <h2 class="text-3xl font-extrabold text-white sm:text-4xl">
          <span class="block">¿Listo para comenzar?</span>
          <span class="block">Regístrate hoy mismo.</span>
        </h2>
        <p class="mt-4 text-lg leading-6 text-teal-200">
          Comienza a gestionar tu negocio de forma más eficiente con Realgestión.
        </p>
        <Link href="/register" class="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-teal-600 bg-white hover:bg-teal-50 sm:w-auto">
          Registrarse gratis
        </Link>
      </div>
    </div>
  </WebLayout>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'
import WebLayout from '@/Layouts/WebLayout.vue'

export default {
  components: {
    Link,
    Head,
    WebLayout
  }
}
</script> 